/**
 * Confirming an email address.
 *
 * The token is read from the fragment, not the query string. A fragment is
 * never sent in the HTTP request, so the token cannot appear in an access log,
 * a proxy's history, or a referrer header on the way to somewhere else. It is
 * the same reasoning as the invite links.
 *
 * The page works signed out on purpose: the link is followed from a mailbox,
 * which is often a different device from the one that registered.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { errorMessage, trpcClient } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";
import { Logo } from "../../components/ui/Logo.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import "./auth.css";

type State =
  | { kind: "working" }
  | { kind: "verified" }
  | { kind: "already" }
  | { kind: "failed"; message: string };

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: "working" });

  useEffect(() => {
    const token = window.location.hash.replace(/^#/, "");
    if (!token) {
      setState({ kind: "failed", message: "That link is missing its code." });
      return;
    }

    void trpcClient.auth.verifyEmail
      .mutate({ token })
      .then((result) => {
        /*
         * Removed from the address bar once spent. It is useless afterwards,
         * but a token sitting in a URL gets bookmarked, screenshotted and
         * pasted into messages, and none of those are places for one to be.
         */
        window.history.replaceState(null, "", window.location.pathname);
        setState(
          result.status === "verified"
            ? { kind: "verified" }
            : { kind: "already" },
        );
      })
      .catch((cause: unknown) => {
        setState({ kind: "failed", message: errorMessage(cause) });
      });
  }, []);

  return (
    <div className="fc-auth">
      <div className="fc-auth__card">
        <Logo size={28} withWordmark className="fc-auth__logo" />

        {state.kind === "working" && (
          <>
            <h1 className="fc-auth__title">Confirming your email</h1>
            <Skeleton height="16px" width="70%" />
          </>
        )}

        {state.kind === "verified" && (
          <>
            <h1 className="fc-auth__title">Email confirmed</h1>
            <p className="fc-auth__tagline">
              Thanks — that address is now attached to your account.
            </p>
          </>
        )}

        {/* Clicking twice is ordinary, so it gets its own answer rather than
            the refusal a forged link would receive. */}
        {state.kind === "already" && (
          <>
            <h1 className="fc-auth__title">Already confirmed</h1>
            <p className="fc-auth__tagline">
              This link has been used before. There is nothing left to do.
            </p>
          </>
        )}

        {state.kind === "failed" && (
          <>
            <h1 className="fc-auth__title">That link didn&rsquo;t work</h1>
            <p className="fc-auth__tagline">{state.message}</p>
            <p className="fc-auth__tagline">
              Links last a day and work once. Sign in and ask for another.
            </p>
          </>
        )}

        <Button
          variant={state.kind === "verified" ? "primary" : "secondary"}
          onClick={() => void navigate({ to: "/" })}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
